/**
 * PageHeader — standard page title block (h1 + optional subtitle +
 * `actions` slot; actions render inline-end on md+, wrap below on mobile —
 * use for CTAs/icon buttons, not navigation).
 */

import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { SxProps, Theme } from '@mui/material/styles';
import { colors } from '../../theme/theme';

export interface PageHeaderProps {
  /** Main heading. Rendered as an `h1` Typography. */
  title: ReactNode;
  /** Optional supporting line. Rendered as an Inter body paragraph. */
  subtitle?: ReactNode;
  /** Inline-end content; wraps below the title on xs. */
  actions?: ReactNode;
  /** Override the heading level (default 1). The visual style stays h1. */
  component?: 'h1' | 'h2';
  /** Optional bottom margin (MUI spacing units). Defaults to 4. */
  mb?: number | string;
  /** Escape hatch for additional outer Box styles. */
  sx?: SxProps<Theme>;
}

export default function PageHeader({
  title,
  subtitle,
  actions,
  component = 'h1',
  mb = 4,
  sx,
}: PageHeaderProps) {
  return (
    <Box
      sx={[
        { mb },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        spacing={{ xs: 2, md: 3 }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="h1"
            component={component}
            sx={{
              color: '#FFFFFF',
              fontSize: { xs: '2.0rem', sm: '2.5rem', md: '3.0rem' },
              lineHeight: 1.08,
              letterSpacing: '-0.03em',
              wordBreak: 'break-word',
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="body1"
              sx={{
                color: colors.neutral[500],
                mt: 1,
                maxWidth: 640,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        {actions && (
          <Box sx={{ flexShrink: 0, width: { xs: '100%', md: 'auto' } }}>
            {actions}
          </Box>
        )}
      </Stack>
    </Box>
  );
}
